import React, { useState, useEffect, useContext } from "react";
import { Alert, Button, Layout } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "../Navbar";
import { DateFilterProvider } from "../../Contexts/DateFilterContext";
import { UserContext } from "../../App";
import "./style.css";
import SidebarManager from "../Sidebar/SidebarManager";
import SidebarAdmin from "../Sidebar/SidebarAdmin";
import SidebarVendor from "../Sidebar/SidebarVendor";

const { Content } = Layout;

const routeTitles = {
  "/dashboard-admin": "Dashboard",
  "/dashboard-manager": "Dashboard",
  "/fleet-management": "Fleet Management",
  "/fleet-management-detail": "Company Details",
  "/fuel-vendors-admin": "Fuel Vendors",
  "/fuel-vendors-manager": "Fuel Vendors",
  "/vendor-detail": "Vendor Detail",
  "/transactions-admin": "Transaction Monitoring",
  "/transactions-manager": "Transactions",
  "/transaction-detail": "Transaction Detail",
  "/transaction-manager-details": "Transaction Detail",
  "/credits-billings-manager": "Credits & Billings",
  "/credits-billings-admin": "Credits & Billings",
  "/credit-adjustments-manager": "Credit Adjustment",
  "/reports-analytics": "Reports & Analytics",
  "/approvals-requests": "Approvals & Requests",
  "/user-management-admin": "User Management",
  "/user-management-manager": "User Management",
  "/settings-permissions-admin": "Settings & Permissions",
  "/settings-permissions-manager": "Settings & Permissions",
  "/settings-permissions-vendor": "Settings & Permissions",
  "/edit-company-onboarding": "Company Onboarding",
  "/edit-vendor-detail": "Vendor Onboarding",
  "/fleet-credit-details": "Fleet Credit Detail",
  "/credit-adjustments": "Credit Adjustment",
  "/vendor-onboarding": "Fuel Vendors onboarding Request",
  "/company-onboarding": "Company Onboarding",
  "/credit-topup-request": "Credit Top-up Request",
  "/subscription-payment-request": "Subscription Payment Request",
  "/user-details": "User Details",
  "/profile-admin": "My Profile",
  "/profile-manager": "My Profile",
  "/profile-vendor": "My Profile",
  "/company-details": "Company Detail",
  "/vendor-details": "Vendor Detail",
  "/audit-trail": "Audit Trail",
  "/qr-management": "QR Management",
  "/qr-active-detail": "QR Management",
  "/fuel-cards": "Fuel Cards",
  "/system-configuration": "System Configuration",
  "/edit-module-price": "System Configuration",
  "/fuel-brand-details": "System Configuration",
  "/add-fuel-brand": "System Configuration",
  "/edit-fuel-brand": "System Configuration",
  "/add-fuel-type": "System Configuration",
  "/edit-fuel-type": "System Configuration",
  "/edit-fleet-manager-document": " Fleet Manager Documents",
  "/add-fleet-manager-document": " Fleet Manager Documents",
  "/add-vehicle-document": "Vehicle Documents",
  "/edit-vehicle-document": "Vehicle Documents",
  "/add-driver-document": "Driver Documents",
  "/edit-driver-document": "Driver Documents",
  "/add-fuel-vendor-document": "Fuel Vendor Documents",
  "/edit-fuel-vendor-document": "Fuel Vendor Documents",
  "/vehicles-manager": "Vehicles",
  "/add-vehicle": "Add Vehicle",
  "/vehicle-detail": "Vehicle Detail",
  "/edit-vehicle": "Edit Vehicle",
  "/drivers-manager": "Drivers",
  "/driver-detail": "Driver Detail",
  "/add-driver": "Add Driver",
  "/edit-driver": "Edit Driver",
  "/reports-admin": "Reports",
  "/reports-manager": "Reports",
  "/user-detail": "User Details",
  "/dashboard-vendor": "Dashboard",
  "/transactions-vendor": "Transactions",
  "/request-adjustments": "Transactions",
  "/transaction-adjustments-detail": "Transaction Adjustment",
  "/transaction-detail-vendor": "Transaction Detail",
  "/fuel-pricing": "Fuel Pricing",
  "/fuel-dispensed": "Fuel Dispensed",
  "/audit-trail-vendor": "Audit Trail",
  "/audit-trail-manager": "Audit Trail",
  "/staff-management": "Staff Management",
  "/staff-detail": "Staff Management",
  "/subscription-payments": "Subscription Payments ",
  "/subscription": "Subscription",
  "/my-subscription": "Subscription",
  "/subscription-payments-admin": "Subscription Payments",
  "/settlement": "Settlement",
};

const STANDALONE_ROUTES = ["/my-subscription"];

// Renewal banner (Fleet Manager): the backend sends show_banner only while
// the subscription is active, expires within 10 days, and the user can renew
// (View & Edit on Subscription Payments). Dismissing hides it for the rest of
// the browser session (sessionStorage, keyed by the expiry date, so a new
// expiry date shows it again).
const BANNER_DISMISS_KEY = "renewal_banner_dismissed_for";

const readDismissed = () => {
  try {
    return sessionStorage.getItem(BANNER_DISMISS_KEY);
  } catch (e) {
    return null;
  }
};

const AppLayout = ({ children, setUser, userType }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [pageTitle, setPageTitle] = useState("Dashboard");
  const [bannerDismissedFor, setBannerDismissedFor] = useState(readDismissed);

  const location = useLocation();
  const navigate = useNavigate();

  const { userData } = useContext(UserContext);
  const reminder = userData?.subscription_renewal_reminder;
  const showRenewalBanner =
    userType === "manager" &&
    !!reminder?.show_banner &&
    bannerDismissedFor !== (reminder.expires_on || "");

  const dismissBanner = () => {
    const key = reminder?.expires_on || "";
    try {
      sessionStorage.setItem(BANNER_DISMISS_KEY, key);
    } catch (e) {
      // storage blocked -- still hide it for this page view
    }
    setBannerDismissedFor(key);
  };
  const isStandalone =
    STANDALONE_ROUTES.includes(location.pathname) ||
    !!userData?.subscription_payment_required;

  useEffect(() => {
    setPageTitle(routeTitles[location.pathname] || "Dashboard");
  }, [location.pathname]);

  const renderSidebar = () => {
    switch (userType) {
      case "admin":
        return (
          <SidebarAdmin
            collapsed={collapsed}
            setCollapsed={setCollapsed}
            setUser={setUser}
          />
        );

      case "manager":
        return (
          <SidebarManager
            collapsed={collapsed}
            setCollapsed={setCollapsed}
            setUser={setUser}
          />
        );

      case "vendor":
        return (
          <SidebarVendor
            collapsed={collapsed}
            setCollapsed={setCollapsed}
            setUser={setUser}
          />
        );

      default:
        return null;
    }
  };
  if (isStandalone) {
    return <DateFilterProvider>{children}</DateFilterProvider>;
  }

  return (
    <Layout style={{ minHeight: "100vh" }}>
      {renderSidebar()}

      <Layout
        className="site-layout"
        style={{
          marginLeft: collapsed ? 80 : 240,
          transition: "margin-left 0.5s",
        }}
      >
        <Navbar
          title={pageTitle}
          collapsed={collapsed}
          setCollapsed={setCollapsed}
        />

        {showRenewalBanner && (
          <Alert
            className="renewal-banner"
            type="warning"
            showIcon
            banner
            closable
            onClose={dismissBanner}
            message={
              reminder.message ||
              "Your subscription expires soon. Renew it to keep access to the portal."
            }
            action={
              <Button
                size="small"
                type="primary"
                onClick={() => navigate("/my-subscription?renew=1")}
              >
                Renew Now
              </Button>
            }
          />
        )}

        <DateFilterProvider>
          <Content>{children}</Content>
        </DateFilterProvider>
      </Layout>
    </Layout>
  );
};

export default AppLayout;
